import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';

/**
 * TriagePane — full-screen batch email classifier.
 * Keyboard-first UX: D=Delete, A=Archive, F=File, O=Open, S=Skip, U=Undo
 */
export default function TriagePane({ onClose }) {
    const { userEmail } = useAddin();

    // ── Data ──
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ── Classification state: { [conversationId]: { action, matter? } } ──
    const [classifications, setClassifications] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [undoStack, setUndoStack] = useState([]);

    // ── Matter search ──
    const [searchMode, setSearchMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchHighlight, setSearchHighlight] = useState(0);
    const [senderMatchLabel, setSenderMatchLabel] = useState('');
    const searchRef = useRef(null);
    const debounceRef = useRef(null);

    // ── Recent matters (persisted to localStorage) ──
    const [recentMatters, setRecentMatters] = useState(() => {
        try { return JSON.parse(localStorage.getItem('ioliberum_triage_recent_matters') || '[]'); }
        catch { return []; }
    });

    // ── Execute state ──
    const [executing, setExecuting] = useState(false);
    const [executeProgress, setExecuteProgress] = useState('');

    // ── Archive mode — folder picker ──
    const [archiveMode, setArchiveMode] = useState(false);
    const [inboxFolders, setInboxFolders] = useState(() => {
        try { return JSON.parse(localStorage.getItem('ioliberum_inbox_folders') || '[]'); }
        catch { return []; }
    });
    const [foldersLoading, setFoldersLoading] = useState(false);

    // ── Skip linked toggle ──
    const [skipLinked, setSkipLinked] = useState(true);

    // ── Folder focus & transactional filter ──
    const [folderFilter, setFolderFilter] = useState('all'); // 'all' | 'inbox' | 'sent'
    const [filterTransactional, setFilterTransactional] = useState(true);
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = oldest first, 'desc' = newest first

    // ── Fetch conversations on mount; auto-load folders if cache is empty ──
    useEffect(() => {
        fetchConversations();
    }, []);

    useEffect(() => {
        if (inboxFolders.length === 0) refreshInboxFolders();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Reset card index when filters or sort change ──
    useEffect(() => {
        setCurrentIndex(0);
    }, [folderFilter, filterTransactional, skipLinked, sortOrder]);

    const fetchConversations = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await webhook.triageInbox({ userEmail });
            // Filter out conversations already actioned in a previous triage session
            const triaged = new Set(JSON.parse(localStorage.getItem('ioliberum_triaged_convs') || '[]'));
            const convs = (res.conversations || []).filter(c => !triaged.has(c.conversationId));
            setConversations(convs);
            setCurrentIndex(0);
            setClassifications({});
            setUndoStack([]);
        } catch (err) {
            setError(err.message || 'Failed to fetch inbox');
            setConversations([]);
        } finally {
            setLoading(false);
        }
    };

    // ── Transactional detection ──
    const TRANSACTIONAL_SUBJECT = /^(reminder:|calendar reminder|accepted:|declined:|tentative:|cancelled:|canceled:|automatic reply:|auto-reply:|auto reply:|out of office:|undeliverable:|delivery failure|delivery status|mail delivery|returned mail|read:|delivery:)|do.?not.?reply|no.?reply/i;
    const TRANSACTIONAL_SENDER = /noreply|no-reply|donotreply|do-not-reply|notifications?@|mailer-daemon|postmaster|calendar-server|calendar-notification|@bounce\./i;
    const isTransactional = (conv) =>
        TRANSACTIONAL_SUBJECT.test(conv.subject || '') ||
        TRANSACTIONAL_SENDER.test(conv.sender || '');

    // ── Derived state ──
    const eligible = conversations
        .filter(c => {
            if (filterTransactional && isTransactional(c)) return false;
            if (folderFilter !== 'all') {
                const sources = c.folderSources ?? (c.folderSource ? [c.folderSource] : []);
                return sources.includes(folderFilter);
            }
            return true;
        })
        .sort((a, b) => {
            const diff = new Date(a.date) - new Date(b.date);
            return sortOrder === 'asc' ? diff : -diff;
        });
    const unclassified = eligible.filter(c =>
        !classifications[c.conversationId] &&
        !(skipLinked && c.linkedMatter)
    );
    const classified = conversations.filter(c => !!classifications[c.conversationId]);
    const eligibleClassified = classified.filter(c => eligible.some(e => e.conversationId === c.conversationId));
    const skippedLinked = skipLinked ? eligible.filter(c => c.linkedMatter && !classifications[c.conversationId]) : [];
    const current = unclassified[currentIndex] || null;
    const totalActionable = eligible.length - skippedLinked.length;
    const progressPct = totalActionable > 0 ? Math.round((eligibleClassified.length / totalActionable) * 100) : 0;
    const transactionalCount = conversations.filter(isTransactional).length;

    // ── Stable ref to current card (avoids stale closures in callbacks) ──
    const currentRef = useRef(null);
    currentRef.current = current;

    // ── Add a matter to the recent-file list ──
    const addRecentMatter = useCallback((matter) => {
        setRecentMatters(prev => {
            const id = matter.matter_id || matter.id;
            const next = [matter, ...prev.filter(m => (m.matter_id || m.id) !== id)].slice(0, 10);
            try { localStorage.setItem('ioliberum_triage_recent_matters', JSON.stringify(next)); } catch {}
            return next;
        });
    }, []);

    // ── Inbox folder refresh ──
    const refreshInboxFolders = useCallback(async () => {
        setFoldersLoading(true);
        try {
            const result = await webhook.getInboxFolders({ userEmail });
            const folders = result?.folders || [];
            setInboxFolders(folders);
            try { localStorage.setItem('ioliberum_inbox_folders', JSON.stringify(folders)); } catch {}
        } catch {}
        finally { setFoldersLoading(false); }
    }, [userEmail]);

    // ── Classification actions ──
    const classify = useCallback((action, matter = null, meta = null) => {
        if (!current) return;
        const convId = current.conversationId;
        setUndoStack(prev => [...prev, { convId, prevIndex: currentIndex }]);
        setClassifications(prev => ({ ...prev, [convId]: { action, matter, meta } }));
        setSearchMode(false);
        setSearchQuery('');
        setSearchResults([]);
        // currentIndex stays — the unclassified array shrinks, so next item auto-appears
        if (currentIndex >= unclassified.length - 1) {
            setCurrentIndex(Math.max(0, currentIndex - 1));
        }
    }, [current, currentIndex, unclassified.length]);

    const handleUndo = useCallback(() => {
        if (undoStack.length === 0) return;
        const last = undoStack[undoStack.length - 1];
        setUndoStack(prev => prev.slice(0, -1));
        setClassifications(prev => {
            const next = { ...prev };
            delete next[last.convId];
            return next;
        });
        setCurrentIndex(last.prevIndex);
    }, [undoStack]);

    const handleSkip = useCallback(() => {
        if (currentIndex < unclassified.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }, [currentIndex, unclassified.length]);

    // ── Open email in Outlook ──
    const handleOpenEmail = useCallback(() => {
        if (!current?.items?.[0]?.id) return;
        try {
            if (typeof Office !== 'undefined' && Office.context?.mailbox?.displayMessageForm) {
                Office.context.mailbox.displayMessageForm(current.items[0].id);
            }
        } catch (err) {
            console.error('Failed to open email:', err);
        }
    }, [current]);

    // ── Archive mode ──
    const enterArchiveMode = useCallback(() => {
        setArchiveMode(true);
    }, []);

    const classifyToFolder = useCallback((folder) => {
        if (folder) {
            classify('move', null, { folderId: folder.id, folderName: folder.displayName });
        } else {
            classify('archive');
        }
        setArchiveMode(false);
    }, [classify]);

    // ── Matter search ──
    const enterFileMode = useCallback(() => {
        setSearchMode(true);
        setSearchQuery('');
        setSearchResults([]);
        setSearchHighlight(0);
        setSenderMatchLabel('');
        setTimeout(() => searchRef.current?.focus(), 50);

        const email = currentRef.current?.senderEmail;
        const senderName = currentRef.current?.sender || email;
        if (email) {
            setSenderMatchLabel(`Searching ${senderName}…`);
            setSearching(true);
            webhook.searchMatters({ userEmail, query: email, surface: 'triage' })
                .then(result => {
                    const matters = result?.matters || [];
                    setSearchResults(matters);
                    setSenderMatchLabel(matters.length > 0 ? `Matched for ${senderName}` : '');
                })
                .catch(() => setSenderMatchLabel(''))
                .finally(() => setSearching(false));
        }
    }, [userEmail]);

    const handleSearchChange = useCallback((e) => {
        const q = e.target.value;
        setSearchQuery(q);
        setSearchHighlight(0);
        setSenderMatchLabel('');
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (q.trim().length < 2) { setSearchResults([]); setSearching(false); return; }
        setSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const result = await webhook.searchMatters({ userEmail, query: q.trim(), surface: 'triage' });
                setSearchResults(result?.matters || []);
            } catch { setSearchResults([]); }
            finally { setSearching(false); }
        }, 300);
    }, [userEmail]);

    const selectMatter = useCallback((matter) => {
        addRecentMatter(matter);
        classify('file', matter);
    }, [classify, addRecentMatter]);

    // ── Keyboard handler ──
    useEffect(() => {
        const handler = (e) => {
            if (archiveMode) {
                if (e.key === 'Escape') setArchiveMode(false);
                return;
            }
            // Don't capture when typing in search
            if (searchMode) {
                if (e.key === 'Escape') { setSearchMode(false); setSearchQuery(''); setSearchResults([]); }
                if (e.key === 'ArrowDown') { e.preventDefault(); setSearchHighlight(h => Math.min(h + 1, searchResults.length - 1)); }
                if (e.key === 'ArrowUp') { e.preventDefault(); setSearchHighlight(h => Math.max(h - 1, 0)); }
                if (e.key === 'Enter' && searchResults[searchHighlight]) { e.preventDefault(); selectMatter(searchResults[searchHighlight]); }
                return;
            }
            const key = e.key.toLowerCase();
            if (key === 'd') classify('delete');
            else if (key === 'a') enterArchiveMode();
            else if (key === 'f') enterFileMode();
            else if (key === 'o') handleOpenEmail();
            else if (key === 's') handleSkip();
            else if (key === 'u') handleUndo();
            else if (key === 'escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [archiveMode, searchMode, searchResults, searchHighlight, classify, enterArchiveMode, enterFileMode, handleOpenEmail, handleSkip, handleUndo, selectMatter, onClose]);

    // ── Execute ──
    const handleExecute = async () => {
        const entries = Object.entries(classifications);
        if (entries.length === 0) return;
        setExecuting(true);

        let linked = 0, archived = 0, moved = 0, deleted = 0, errors = 0;
        for (const [convId, { action, matter, meta }] of entries) {
            const conv = conversations.find(c => c.conversationId === convId);
            if (!conv) continue;

            try {
                if (action === 'file' && matter) {
                    setExecuteProgress(`Linking ${++linked}... ${conv.subject?.slice(0, 40)}`);
                    await webhook.linkMatter({
                        conversationId: convId,
                        userEmail,
                        senderEmail: conv.senderEmail || undefined,
                        matterId: matter.matter_id || matter.id,
                        matterNumber: matter.matter_number || matter.number,
                        matterDescription: matter.matter_description || matter.name || '',
                        source: 'triage',
                        linkSource: 'triage_batch',
                    });
                } else if (action === 'move' && meta?.folderId) {
                    setExecuteProgress(`Moving ${++moved} to ${meta.folderName}... ${conv.subject?.slice(0, 30)}`);
                    await webhook.moveEmails({ userEmail, conversationId: convId, messageIds: conv.items?.map(i => i.id), destination: meta.folderId });
                } else if (action === 'archive') {
                    setExecuteProgress(`Archiving ${++archived}... ${conv.subject?.slice(0, 40)}`);
                    await webhook.moveEmails({ userEmail, conversationId: convId, messageIds: conv.items?.map(i => i.id), destination: 'archive' });
                } else if (action === 'delete') {
                    setExecuteProgress(`Deleting ${++deleted}... ${conv.subject?.slice(0, 40)}`);
                    await webhook.deleteEmails({ userEmail, conversationId: convId, messageIds: conv.items?.map(i => i.id) });
                }
            } catch {
                errors++;
            }
        }

        // Cache actioned conversation IDs so they don't reappear in the next triage session
        const actionedIds = Object.keys(classifications);
        if (actionedIds.length > 0) {
            try {
                const existing = JSON.parse(localStorage.getItem('ioliberum_triaged_convs') || '[]');
                const updated = [...new Set([...existing, ...actionedIds])].slice(-1000);
                localStorage.setItem('ioliberum_triaged_convs', JSON.stringify(updated));
            } catch {}
            // Remove from current session state immediately
            setConversations(prev => prev.filter(c => !actionedIds.includes(c.conversationId)));
        }

        // Trigger full scan if any were filed
        if (linked > 0) {
            setExecuteProgress('Triggering full scan...');
            try {
                const token = localStorage.getItem('ioliberum_token');
                await fetch('/api/webhook/trigger-full-scan', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({}),
                });
            } catch { /* best effort */ }
        }

        setExecuteProgress(`Done! Filed: ${linked}, Archived: ${archived}${moved ? `, Moved: ${moved}` : ''}, Deleted: ${deleted}${errors ? `, Errors: ${errors}` : ''}`);
        setExecuting(false);
        setTimeout(() => onClose(), 3000);
    };

    // ── Action badge helper ──
    const actionIcon = (a) => a === 'file' ? '📁' : a === 'archive' ? '📦' : a === 'move' ? '📂' : '🗑';
    const actionLabel = (c) => {
        const cl = classifications[c.conversationId];
        if (!cl) return null;
        if (cl.action === 'file') return `→ ${cl.matter?.matter_number || cl.matter?.number || 'Matter'}`;
        if (cl.action === 'archive') return '→ Archive';
        if (cl.action === 'move') return `→ ${cl.meta?.folderName || 'Folder'}`;
        return '→ Delete';
    };

    // ── Styles ──
    const S = {
        container: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#0a0e1a', zIndex: 1000, display: 'flex', flexDirection: 'column', fontFamily: 'Cabin, Inter, sans-serif', color: '#e0e0e0' },
        header: { padding: '12px 16px', background: 'linear-gradient(135deg, #00204a 0%, #0d3b66 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)' },
        title: { margin: 0, fontSize: '16px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' },
        counter: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: 400 },
        progress: { padding: '0 16px', paddingTop: '10px' },
        progressBar: { height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' },
        progressFill: (pct) => ({ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #00b894, #00cec9)', borderRadius: '2px', transition: 'width 0.3s ease' }),
        progressText: { fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '4px', textAlign: 'right' },
        body: { flex: 1, overflowY: 'auto', padding: '12px 16px' },
        currentCard: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '16px', marginBottom: '12px' },
        cardLabel: { fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' },
        cardSubject: { fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: '6px', lineHeight: 1.3 },
        cardMeta: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', display: 'flex', justifyContent: 'space-between' },
        cardThreads: { fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' },
        actions: { display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' },
        btn: (bg) => ({ padding: '8px 14px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: '#fff', background: bg, display: 'flex', alignItems: 'center', gap: '4px', transition: 'opacity 0.15s', flex: '1 1 auto', justifyContent: 'center', minWidth: '70px' }),
        searchBox: { marginBottom: '12px' },
        searchInput: { width: '100%', padding: '8px 12px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '12px', outline: 'none', boxSizing: 'border-box' },
        resultItem: (hl) => ({ padding: '8px 12px', cursor: 'pointer', borderRadius: '4px', background: hl ? 'rgba(0,184,148,0.15)' : 'transparent', borderBottom: '1px solid rgba(255,255,255,0.04)' }),
        resultName: { fontSize: '11px', fontWeight: 600, color: '#fff' },
        resultMeta: { fontSize: '9px', color: 'rgba(255,255,255,0.4)' },
        queueLabel: { fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.25)', marginBottom: '6px', marginTop: '8px' },
        queueItem: (done) => ({ padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', borderBottom: '1px solid rgba(255,255,255,0.04)', opacity: done ? 0.5 : 1, color: done ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.7)' }),
        badge: (color) => ({ fontSize: '9px', padding: '1px 6px', borderRadius: '3px', background: color, color: '#fff', fontWeight: 700 }),
        linkedBadge: { fontSize: '8px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(0,184,148,0.2)', color: '#00b894', fontWeight: 600 },
        execBtn: { padding: '10px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #00b894, #00cec9)', width: '100%', marginTop: '8px' },
        recentBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '5px', background: 'rgba(255,255,255,0.03)', cursor: 'pointer', width: '100%', textAlign: 'left', marginBottom: '3px' },
        folderBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '5px', background: 'rgba(255,255,255,0.03)', cursor: 'pointer', width: '100%', textAlign: 'left', marginBottom: '3px', color: '#e0e0e0', fontSize: '11px' },
        archiveStdBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', border: '1px solid rgba(99,110,114,0.5)', borderRadius: '6px', background: 'rgba(99,110,114,0.2)', cursor: 'pointer', width: '100%', textAlign: 'left', marginBottom: '8px', color: '#fff', fontSize: '12px', fontWeight: 700 },
        sectionLabel: { fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.25)', marginBottom: '5px', marginTop: '4px' },
        filterBar: { padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' },
        folderTab: (active) => ({ padding: '3px 10px', border: '1px solid', borderColor: active ? '#00b894' : 'rgba(255,255,255,0.12)', borderRadius: '4px', background: active ? 'rgba(0,184,148,0.15)' : 'transparent', color: active ? '#00b894' : 'rgba(255,255,255,0.45)', fontSize: '10px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }),
        filterDivider: { width: '1px', height: '14px', background: 'rgba(255,255,255,0.1)', margin: '0 2px' },
        footer: { padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)' },
        shortcutBar: { display: 'flex', gap: '10px', justifyContent: 'center', fontSize: '9px', color: 'rgba(255,255,255,0.3)' },
        kbd: { background: 'rgba(255,255,255,0.08)', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '9px', color: 'rgba(255,255,255,0.5)' },
    };

    return (
        <div style={S.container}>
            {/* Header */}
            <div style={S.header}>
                <h2 style={S.title}>
                    ⚡ Batch Triage
                    <span style={S.counter}>{classified.length} / {totalActionable}</span>
                </h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {classified.length > 0 && !executing && (
                        <button style={{ ...S.btn('linear-gradient(135deg, #00b894, #00cec9)'), flex: 'none', padding: '6px 16px' }} onClick={handleExecute}>
                            Execute ({classified.length})
                        </button>
                    )}
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '20px' }}>✕</button>
                </div>
            </div>

            {/* Progress bar */}
            {conversations.length > 0 && (
                <div style={S.progress}>
                    <div style={S.progressBar}><div style={S.progressFill(progressPct)} /></div>
                    <div style={S.progressText}>{progressPct}% classified</div>
                </div>
            )}

            {/* Filter bar */}
            {!loading && !error && conversations.length > 0 && (
                <div style={S.filterBar}>
                    {['all', 'inbox', 'sent'].map(f => (
                        <button key={f} style={S.folderTab(folderFilter === f)} onClick={() => setFolderFilter(f)}>
                            {f === 'all' ? 'All' : f === 'inbox' ? 'Inbox' : 'Sent'}
                        </button>
                    ))}
                    <div style={S.filterDivider} />
                    <button style={S.folderTab(false)} onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}>
                        {sortOrder === 'asc' ? '↑ Oldest' : '↓ Newest'}
                    </button>
                    <div style={S.filterDivider} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: filterTransactional ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)', cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={filterTransactional} onChange={() => setFilterTransactional(v => !v)} style={{ margin: 0 }} />
                        Hide transactional
                        {transactionalCount > 0 && <span style={{ color: 'rgba(255,255,255,0.25)' }}>({transactionalCount})</span>}
                    </label>
                </div>
            )}

            {/* Body */}
            <div style={S.body}>
                {loading && <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.4)' }}>Scanning inbox + sent items…</div>}
                {error && <div style={{ textAlign: 'center', padding: '20px', color: '#ff7675' }}>⚠ {error}<br /><button onClick={fetchConversations} style={{ ...S.btn('rgba(255,255,255,0.1)'), marginTop: '8px', display: 'inline-flex' }}>Retry</button></div>}

                {!loading && !error && conversations.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.3)' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                        No conversations found.
                    </div>
                )}

                {executing && <div style={{ textAlign: 'center', padding: '30px 0', color: '#00cec9' }}><div className="btn-spinner" style={{ margin: '0 auto 12px' }} />{executeProgress}</div>}

                {!loading && !error && !executing && conversations.length > 0 && (
                    <>
                        {/* Skip linked toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '10px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={skipLinked} onChange={() => setSkipLinked(!skipLinked)} />
                            Skip already-linked ({conversations.filter(c => c.linkedMatter).length})
                        </label>

                        {/* Current card */}
                        {current ? (
                            <>
                                <div style={S.currentCard}>
                                    <div style={S.cardLabel}>Current — {unclassified.indexOf(current) + 1} of {unclassified.length} remaining</div>
                                    <div style={S.cardSubject}>{current.subject || 'No Subject'}</div>
                                    <div style={S.cardMeta}>
                                        <span>{current.sender || 'Unknown'}</span>
                                        <span>{current.date ? new Date(current.date).toLocaleDateString() : ''}</span>
                                    </div>
                                    <div style={S.cardThreads}>
                                        {current.threadCount || current.items?.length || 1} email{(current.threadCount || current.items?.length || 1) !== 1 ? 's' : ''} in thread
                                        {current.folderSource && <span> · {current.folderSource}</span>}
                                    </div>
                                    {current.linkedMatter && (
                                        <div style={{ marginTop: '6px' }}>
                                            <span style={S.linkedBadge}>Already linked: {current.linkedMatter.matter_number || current.linkedMatter.number}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Action buttons / archive picker / file search */}
                                {!searchMode && !archiveMode ? (
                                    <div style={S.actions}>
                                        <button style={S.btn('#d63031')} onClick={() => classify('delete')}>🗑 Delete <span style={S.kbd}>D</span></button>
                                        <button style={S.btn('#636e72')} onClick={enterArchiveMode}>📦 Archive <span style={S.kbd}>A</span></button>
                                        <button style={S.btn('#0984e3')} onClick={enterFileMode}>📁 File <span style={S.kbd}>F</span></button>
                                        <button style={S.btn('rgba(255,255,255,0.08)')} onClick={handleOpenEmail} title="Open in Outlook">👁 <span style={S.kbd}>O</span></button>
                                        <button style={S.btn('rgba(255,255,255,0.05)')} onClick={handleSkip}>Skip <span style={S.kbd}>S</span></button>
                                    </div>
                                ) : archiveMode ? (
                                    <div style={{ marginBottom: '12px' }}>
                                        {/* Standard archive */}
                                        <button style={S.archiveStdBtn} onClick={() => classifyToFolder(null)}>
                                            📦 Archive <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', fontWeight: 400, marginLeft: 'auto' }}>Archive folder</span>
                                        </button>

                                        {/* Inbox subfolders */}
                                        {inboxFolders.length > 0 && (
                                            <>
                                                <div style={S.sectionLabel}>Move to inbox folder</div>
                                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                    {inboxFolders.map(f => (
                                                        <button key={f.id} style={S.folderBtn} onClick={() => classifyToFolder(f)}>
                                                            <span>📂</span>
                                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.displayName}</span>
                                                            {f.unreadItemCount > 0 && (
                                                                <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>{f.unreadItemCount} unread</span>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                        {inboxFolders.length === 0 && !foldersLoading && (
                                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' }}>No folders cached yet.</div>
                                        )}

                                        {/* Bottom bar */}
                                        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                                            <button style={{ ...S.btn('rgba(255,255,255,0.06)'), fontSize: '10px', padding: '6px 10px' }} onClick={refreshInboxFolders} disabled={foldersLoading}>
                                                {foldersLoading ? 'Loading…' : '⟳ Refresh'}
                                            </button>
                                            <button style={{ ...S.btn('rgba(255,255,255,0.04)'), fontSize: '10px', padding: '6px 10px' }} onClick={() => setArchiveMode(false)}>
                                                Cancel <span style={S.kbd}>Esc</span>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={S.searchBox}>
                                        <input
                                            ref={searchRef}
                                            style={S.searchInput}
                                            placeholder="Search matter by name, number, or client…"
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            autoFocus
                                        />

                                        {/* Sender lookup / live search status */}
                                        {searching && (
                                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>
                                                {senderMatchLabel || 'Searching…'}
                                            </div>
                                        )}

                                        {/* Sender-matched or live-search results */}
                                        {!searching && searchResults.length > 0 && (
                                            <>
                                                {senderMatchLabel && (
                                                    <div style={S.sectionLabel}>{senderMatchLabel}</div>
                                                )}
                                                <div style={{ maxHeight: '160px', overflowY: 'auto', marginTop: '4px' }}>
                                                    {searchResults.map((m, i) => (
                                                        <div key={m.matter_id || m.id || i} style={S.resultItem(i === searchHighlight)} onClick={() => selectMatter(m)} onMouseEnter={() => setSearchHighlight(i)}>
                                                            <div style={S.resultName}>{m.name || m.matter_description}</div>
                                                            <div style={S.resultMeta}>{m.number || m.matter_number}{m.client_name ? ` · ${m.client_name}` : ''}{m.status ? ` · ${m.status}` : ''}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                        {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' }}>No matters found</div>
                                        )}

                                        {/* Quick File — recent matters */}
                                        {recentMatters.length > 0 && !searchQuery && (
                                            <>
                                                <div style={S.sectionLabel}>Quick File</div>
                                                {recentMatters.map((m, i) => (
                                                    <button key={m.matter_id || m.id || i} style={S.recentBtn} onClick={() => selectMatter(m)}>
                                                        <span style={{ fontWeight: 700, color: '#00cec9', fontSize: '10px', flexShrink: 0 }}>
                                                            {m.matter_number || m.number}
                                                        </span>
                                                        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {m.name || m.matter_description || ''}
                                                        </span>
                                                    </button>
                                                ))}
                                            </>
                                        )}

                                        <button style={{ ...S.btn('rgba(255,255,255,0.05)'), marginTop: '8px', width: '100%' }} onClick={() => { setSearchMode(false); setSearchQuery(''); setSearchResults([]); setSenderMatchLabel(''); }}>
                                            Cancel <span style={S.kbd}>Esc</span>
                                        </button>
                                    </div>
                                )}

                                {/* Undo */}
                                {undoStack.length > 0 && (
                                    <button style={{ ...S.btn('rgba(255,255,255,0.05)'), width: '100%', marginBottom: '10px' }} onClick={handleUndo}>
                                        ↩ Undo last <span style={S.kbd}>U</span>
                                    </button>
                                )}
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.4)' }}>
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎉</div>
                                All conversations classified!
                                {classified.length > 0 && (
                                    <button style={{ ...S.execBtn, marginTop: '16px', maxWidth: '250px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }} onClick={handleExecute}>
                                        Execute {classified.length} actions
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Classified queue */}
                        {classified.length > 0 && (
                            <>
                                <div style={S.queueLabel}>Classified ({classified.length})</div>
                                <div style={{ maxHeight: '180px', overflowY: 'auto', borderRadius: '6px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    {classified.map(c => (
                                        <div key={c.conversationId} style={S.queueItem(true)}>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '8px' }}>
                                                {actionIcon(classifications[c.conversationId].action)} {c.subject || 'No Subject'}
                                            </span>
                                            <span style={{ flexShrink: 0, fontSize: '9px', color: 'rgba(255,255,255,0.3)' }}>
                                                {actionLabel(c)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Footer — keyboard shortcuts */}
            <div style={S.footer}>
                <div style={S.shortcutBar}>
                    <span><span style={S.kbd}>D</span> Delete</span>
                    <span><span style={S.kbd}>A</span> Archive</span>
                    <span><span style={S.kbd}>F</span> File</span>
                    <span><span style={S.kbd}>O</span> Open</span>
                    <span><span style={S.kbd}>S</span> Skip</span>
                    <span><span style={S.kbd}>U</span> Undo</span>
                    <span><span style={S.kbd}>Esc</span> Close</span>
                </div>
            </div>
        </div>
    );
}
